import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { RequiredAsterisk } from "../../cmps/forms/required-asterisk"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"

interface JobAction {
    job_name: string
    arguments: Record<string, string>
    timeout?: number
}

interface GlueTriggerFormModalProps {
    triggerToEdit?: any
    onSuccess: () => void
    onClose: () => void
}

const DAYS_OF_WEEK = [
    { id: 'MON', name: 'Mon' },
    { id: 'TUE', name: 'Tue' },
    { id: 'WED', name: 'Wed' },
    { id: 'THU', name: 'Thu' },
    { id: 'FRI', name: 'Fri' },
    { id: 'SAT', name: 'Sat' },
    { id: 'SUN', name: 'Sun' },
]

const DEFAULT_JOB_ARGUMENTS: Record<string, string> = {
    '--entity_name': '',
    '--system_name': '',
}

function parseCronToFields(cronExpr: string): { hour: string, minute: string, days: string[] } {
    const parts = cronExpr.trim().split(/\s+/)
    if (parts.length < 6) return { hour: '0', minute: '0', days: [] }
    const [minute, hour, , , dayOfWeek] = parts
    const days = (dayOfWeek && dayOfWeek !== '?' && dayOfWeek !== '*')
        ? dayOfWeek.split(',').map(d => d.toUpperCase())
        : []
    return { hour: hour || '0', minute: minute || '0', days }
}

function fieldsToCron(hour: string, minute: string, days: string[]): string {
    const dayExpr = days.length > 0 ? days.join(',') : '?'
    const domExpr = days.length > 0 ? '?' : '*'
    return `${minute} ${hour} ${domExpr} * ${dayExpr} *`
}

const STATE_OPTIONS = [
    { id: 'ACTIVATED', name: 'Activated' },
    { id: 'DEACTIVATED', name: 'Deactivated' },
]

export const GlueTriggerFormModal: React.FC<GlueTriggerFormModalProps> = ({
    triggerToEdit, onSuccess, onClose
}) => {
    const isEditMode = !!triggerToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [pendingConfirm, setPendingConfirm] = useState(false)

    const [form, setForm] = useState(() => {
        if (isEditMode) {
            const cronFields = triggerToEdit.schedule_type === 'cron' && triggerToEdit.schedule_details?.expression
                ? parseCronToFields(triggerToEdit.schedule_details.expression)
                : { hour: '10', minute: '0', days: [] as string[] }
            return {
                name: triggerToEdit.trigger_name || '',
                description: triggerToEdit.description || '',
                cronHour: cronFields.hour,
                cronMinute: cronFields.minute,
                cronDays: cronFields.days,
                state: triggerToEdit.state || 'CREATED',
                jobActions: (triggerToEdit.job_actions || []).map((a: JobAction) => ({
                    job_name: a.job_name,
                    arguments: { ...a.arguments },
                    timeout: a.timeout,
                })),
            }
        }
        return {
            name: '',
            description: '',
            cronHour: '10',
            cronMinute: '0',
            cronDays: [] as string[],
            state: 'ACTIVATED',
            jobActions: [{ job_name: '', arguments: { ...DEFAULT_JOB_ARGUMENTS }, timeout: undefined as number | undefined }],
        }
    })

    const [glueJobNames, setGlueJobNames] = useState<string[]>([])

    useEffect(() => {
        httpService.get('/glue-job-names')
            .then((res: any) => setGlueJobNames(res.job_names || []))
            .catch(() => setGlueJobNames([]))
    }, [])

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        setPendingConfirm(false)
    }

    const onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        setPendingConfirm(false)
    }

    const toggleCronDay = (dayId: string) => {
        setForm(prev => {
            const days = prev.cronDays.includes(dayId)
                ? prev.cronDays.filter(d => d !== dayId)
                : [...prev.cronDays, dayId]
            return { ...prev, cronDays: days }
        })
        setPendingConfirm(false)
    }

    const onChangeJobAction = (index: number, field: string, value: string) => {
        setForm(prev => {
            const updated = [...prev.jobActions]
            if (field === 'job_name') {
                updated[index] = { ...updated[index], job_name: value }
            }
            return { ...prev, jobActions: updated }
        })
        setPendingConfirm(false)
    }

    const onChangeJobArgument = (jobIndex: number, key: string, value: string) => {
        setForm(prev => {
            const updated = [...prev.jobActions]
            updated[jobIndex] = {
                ...updated[jobIndex],
                arguments: { ...updated[jobIndex].arguments, [key]: value }
            }
            return { ...prev, jobActions: updated }
        })
    }

    const addJobArgument = (jobIndex: number) => {
        const key = prompt('Argument key (e.g., --entity_name):')
        if (!key) return
        onChangeJobArgument(jobIndex, key, '')
    }

    const removeJobArgument = (jobIndex: number, key: string) => {
        setForm(prev => {
            const updated = [...prev.jobActions]
            const args = { ...updated[jobIndex].arguments }
            delete args[key]
            updated[jobIndex] = { ...updated[jobIndex], arguments: args }
            return { ...prev, jobActions: updated }
        })
    }

    const addJobAction = () => {
        setForm(prev => ({
            ...prev,
            jobActions: [...prev.jobActions, { job_name: '', arguments: { ...DEFAULT_JOB_ARGUMENTS }, timeout: undefined }]
        }))
    }

    const removeJobAction = (index: number) => {
        if (form.jobActions.length <= 1) return
        setForm(prev => ({
            ...prev,
            jobActions: prev.jobActions.filter((_: any, i: number) => i !== index)
        }))
    }

    const TRIGGER_NAME_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/
    const TRIGGER_NAME_MAX_LENGTH = 255

    const validate = (): string | null => {
        const name = form.name.trim()
        if (!name) return 'Schedule name is required'
        if (name.length > TRIGGER_NAME_MAX_LENGTH) return `Schedule name must be ${TRIGGER_NAME_MAX_LENGTH} characters or less`
        if (!TRIGGER_NAME_REGEX.test(name)) return 'Schedule name must be lowercase, use only letters, numbers, and hyphens (e.g., company-daily-etl)'
        if (name.includes('--')) return 'Schedule name cannot contain consecutive hyphens'
        if (form.jobActions.length === 0) return 'At least one job action is required'
        for (const action of form.jobActions) {
            if (!action.job_name.trim()) return 'All job actions must have a job name'
        }
        return null
    }

    const buildScheduleExpression = (): string => {
        const cronExpr = fieldsToCron(form.cronHour, form.cronMinute, form.cronDays)
        return `cron(${cronExpr})`
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            const payload: any = {
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                type: 'SCHEDULED',
                schedule: buildScheduleExpression(),
                actions: form.jobActions.map((a: any) => ({
                    JobName: a.job_name.trim(),
                    Arguments: a.arguments,
                    ...(a.timeout ? { Timeout: a.timeout } : {}),
                })),
            }
            if (form.state === 'ACTIVATED') payload.startOnCreation = true

            if (isEditMode) {
                const realTriggerNames: string[] = (triggerToEdit._grouped_triggers || [triggerToEdit])
                    .map((t: any) => t.trigger_name)

                const stateChanged = form.state !== triggerToEdit.state
                const origCron = triggerToEdit.schedule_details?.expression
                    ? parseCronToFields(triggerToEdit.schedule_details.expression)
                    : { hour: '10', minute: '0', days: [] as string[] }

                const normalizeActions = (actions: any[]) => actions.map((a: any) => ({
                    JobName: a.JobName || a.job_name,
                    Arguments: Object.fromEntries(Object.entries(a.Arguments || a.arguments || {}).sort()),
                    ...(a.Timeout || a.timeout ? { Timeout: a.Timeout || a.timeout } : {})
                }))

                const configChanged = form.description !== (triggerToEdit.description || '')
                    || form.cronHour !== origCron.hour
                    || form.cronMinute !== origCron.minute
                    || JSON.stringify(form.cronDays.slice().sort()) !== JSON.stringify(origCron.days.slice().sort())
                    || form.jobActions.length !== (triggerToEdit.job_actions || []).length
                    || JSON.stringify(normalizeActions(payload.actions)) !== JSON.stringify(normalizeActions(triggerToEdit.job_actions || []))

                if (configChanged) {
                    if (!pendingConfirm) {
                        setPendingConfirm(true)
                        setIsSubmitting(false)
                        return
                    }
                    setPendingConfirm(false)
                    await httpService.put('/glue-triggers', {
                        trigger_name: triggerToEdit.trigger_name,
                        real_trigger_names: realTriggerNames,
                        ...payload,
                    })
                }

                if (stateChanged || (configChanged && form.state === 'ACTIVATED')) {
                    await httpService.post('/glue-triggers/toggle-state', {
                        trigger_name: form.name.trim(),
                        real_trigger_names: configChanged ? undefined : realTriggerNames,
                        activate: form.state === 'ACTIVATED',
                    })
                }

                const msg: UserMsg = { type: 'success', txt: 'Schedule updated successfully' }
                dispatch(setUserMsg({ msg }))
            } else {
                await httpService.post('/glue-triggers', payload)
                const msg: UserMsg = { type: 'success', txt: 'Schedule created successfully' }
                dispatch(setUserMsg({ msg }))
            }
            onSuccess()
        } catch (err: any) {
            console.error('Glue trigger mutation error:', err)
            let errorMsg = isEditMode ? 'Failed updating schedule' : 'Failed creating schedule'
            try {
                const body = await err?.json?.()
                if (body?.duplicates) {
                    errorMsg = `Duplicate found in other schedules: ${body.duplicates.join(', ')}`
                } else if (body?.error) {
                    errorMsg = body.error
                }
            } catch { /* ignore parse errors */ }
            const msg: UserMsg = { type: 'danger', txt: errorMsg }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit ETL Schedule' : 'Create ETL Schedule'

    return <div ref={trapRef}>
        <section className="glue-trigger-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Schedule Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="name"
                                label="Schedule Name"
                                isRequired={true}
                                isDisabled={isEditMode}
                                value={form.name}
                                onChange={onChangeText}
                                placeholder="e.g., company-daily-etl"
                            />
                            {!isEditMode && <div className="field-hint">Lowercase letters, numbers, and hyphens only</div>}
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="description"
                                label="Description"
                                isRequired={false}
                                value={form.description}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <div>
                                <label htmlFor="cronTime">Time (UTC)</label>
                                <RequiredAsterisk />
                            </div>
                            <input
                                type="time"
                                id="cronTime"
                                name="cronTime"
                                value={`${String(form.cronHour).padStart(2, '0')}:${String(form.cronMinute).padStart(2, '0')}`}
                                onChange={(e) => {
                                    const [h, m] = e.target.value.split(':')
                                    setForm(prev => ({ ...prev, cronHour: String(parseInt(h, 10)), cronMinute: String(parseInt(m, 10)) }))
                                }}
                            />
                            <span></span>
                        </div>
                        <div className="field field-full-width">
                            <div><label>Days</label></div>
                            <div className="day-picker">
                                {DAYS_OF_WEEK.map(day => (
                                    <button
                                        key={day.id}
                                        type="button"
                                        className={`day-btn ${form.cronDays.includes(day.id) ? 'active' : ''}`}
                                        onClick={() => toggleCronDay(day.id)}
                                    >
                                        {day.name}
                                    </button>
                                ))}
                            </div>
                            <div className="day-hint">No days selected = runs daily</div>
                        </div>
                        <div className="field">
                            <div>
                                <label htmlFor="state">State</label>
                                <RequiredAsterisk />
                            </div>
                            <select name="state" id="state" value={form.state} onChange={onChangeSelect}>
                                {STATE_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                            <span></span>
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">
                        Job Actions
                        <button className="add-job-btn" onClick={addJobAction} type="button">+ Add Job</button>
                    </div>
                    {form.jobActions.map((action: any, jobIndex: number) => (
                        <div key={jobIndex} className="job-action-card">
                            <div className="job-action-header">
                                <span className="job-action-title">Job {jobIndex + 1}</span>
                                {form.jobActions.length > 1 && (
                                    <button className="remove-job-btn" onClick={() => removeJobAction(jobIndex)} type="button">Remove</button>
                                )}
                            </div>
                            <div className="form-row">
                                <div className="field">
                                    <div>
                                        <label htmlFor={`job_name_${jobIndex}`}>Job Name</label>
                                        <RequiredAsterisk />
                                    </div>
                                    <select
                                        id={`job_name_${jobIndex}`}
                                        value={action.job_name}
                                        onChange={(e) => onChangeJobAction(jobIndex, 'job_name', e.target.value)}
                                    >
                                        <option value="">Select a job</option>
                                        {glueJobNames.map(name => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                    <span></span>
                                </div>
                            </div>
                            <div className="job-arguments">
                                <div className="job-arguments-header">
                                    <span>Arguments</span>
                                    <button className="add-arg-btn" onClick={() => addJobArgument(jobIndex)} type="button">+ Add Argument</button>
                                </div>
                                {Object.entries(action.arguments).map(([key, value]) => (
                                    <div key={key} className="argument-row">
                                        <span className="arg-key">{key}</span>
                                        <input
                                            type="text"
                                            value={value as string}
                                            onChange={(e) => onChangeJobArgument(jobIndex, key, e.target.value)}
                                            placeholder="Value"
                                        />
                                        <button className="remove-arg-btn" onClick={() => removeJobArgument(jobIndex, key)} type="button">&times;</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="form-actions">
                    {pendingConfirm && (
                        <div className="confirm-bar">
                            <span>This will recreate the schedule(s) in AWS.</span>
                            <div className="confirm-bar-actions">
                                <button className="confirm-btn" onClick={onSubmit}>Confirm</button>
                                <button className="cancel-btn" onClick={() => setPendingConfirm(false)}>Cancel</button>
                            </div>
                        </div>
                    )}
                    {!pendingConfirm && !isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
